import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function MediaUploadsAdmin({ onUploadsChanged }) {
  const [uploads, setUploads] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    fetch(`${API}/admin/uploads`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setUploads(d.uploads || []); onUploadsChanged?.(d.uploads || []); })
      .catch(() => toast.error("Could not load uploaded media."));
  }, [onUploadsChanged]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a photo or video first."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("notes", notes);
      const res = await fetch(`${API}/admin/uploads`, { method: "POST", credentials: "include", body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Upload failed.");
      toast.success("Uploaded — attach it to a video draft below or in the YouTube studio.");
      setNotes(""); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const res = await fetch(`${API}/admin/uploads/${encodeURIComponent(id)}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { toast.success("Removed."); load(); } else toast.error("Could not remove upload.");
  };

  return (
    <section className="glass rounded-3xl p-6 md:p-7 mb-8 border border-gp-blue/25" data-testid="admin-media-uploads">
      <div>
        <h2 className="font-unbounded font-bold text-xl">Media library (real footage for Yorick)</h2>
        <p className="text-sm text-white/55 mt-1">Upload real photos/video (e.g. building GamePlatPlay) with a short caption of what's in it. Attach them when generating a video below so Yorick's script is grounded in the real story, not made up.</p>
      </div>

      <form onSubmit={upload} className="mt-4 space-y-2">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" className="block w-full text-sm text-white/70 file:mr-3 file:rounded-full file:border-0 file:bg-gp-blue file:px-4 file:py-2 file:text-white" />
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What's in this? e.g. 'Us coding the first prototype in a coffee shop, spring 2025.'" className="bg-black/25 border-white/10" rows={2} />
        <Button type="submit" disabled={busy} className="rounded-full bg-gp-blue">
          {busy ? <Loader2 className="animate-spin mr-2" size={15} /> : <Upload className="mr-2" size={15} />}Upload
        </Button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
        {uploads.map((u) => <div key={u.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
          {u.kind === "image"
            ? <img src={`${API}/admin/uploads/${u.id}/file`} alt={u.notes || u.original_filename} className="rounded-lg w-full h-28 object-cover" />
            : <video src={`${API}/admin/uploads/${u.id}/file`} controls className="rounded-lg w-full h-28 object-cover" />}
          <p className="text-xs text-white/70 mt-2 line-clamp-2">{u.notes || "(no caption)"}</p>
          <div className="flex items-center justify-between mt-1"><span className="text-xs text-white/40">{formatBytes(u.size_bytes)}</span><button type="button" onClick={() => remove(u.id)} className="text-white/40 hover:text-red-400"><Trash2 size={13} /></button></div>
        </div>)}
        {uploads.length === 0 && <p className="text-sm text-white/40">Nothing uploaded yet.</p>}
      </div>
    </section>
  );
}
